import { useSearchParams, useNavigate } from "react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, XCircle, Building2, Mail } from "lucide-react";
import { useGetInvitationByTokenQuery, useAcceptInvitationMutation } from "@/redux/api/adminInvitations.rtkq";
import { useAuthContext } from "@/hooks/useAuthContext";
import { toast } from "sonner";

const InvitationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const { user } = useAuthContext();

  const { data: invitation, isLoading, error } = useGetInvitationByTokenQuery(token!, { skip: !token });
  const [acceptInvitation, { isLoading: accepting }] = useAcceptInvitationMutation();

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" dir="rtl">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center py-12">
            <XCircle className="h-12 w-12 text-red-400 mb-4" />
            <h2 className="text-lg font-semibold mb-2">קישור לא תקין</h2>
            <p className="text-sm text-muted-foreground">חסר טוקן הזמנה בכתובת</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !invitation) {
    const errMsg = (error as any)?.data?.message || "ההזמנה לא נמצאה או שפג תוקפה";
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" dir="rtl">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center py-12">
            <XCircle className="h-12 w-12 text-red-400 mb-4" />
            <h2 className="text-lg font-semibold mb-2">שגיאה בהזמנה</h2>
            <p className="text-sm text-muted-foreground">{errMsg}</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/login")}>
              חזור להתחברות
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleAccept = async () => {
    try {
      const result = await acceptInvitation(token).unwrap();
      toast.success("ההזמנה התקבלה בהצלחה!");

      if (result.officeId) {
        localStorage.setItem("officeId", result.officeId);
      }

      // Navigate to main app
      navigate("/");
    } catch (err: any) {
      toast.error(err?.data?.message || "שגיאה בקבלת ההזמנה");
    }
  };

  const handleLogin = () => {
    navigate(`/login?redirect=${encodeURIComponent(`/invitation?token=${token}`)}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" dir="rtl">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              {invitation.isSystemInvite ? (
                <Building2 className="h-8 w-8 text-primary" />
              ) : (
                <Mail className="h-8 w-8 text-primary" />
              )}
            </div>
          </div>
          <CardTitle className="text-xl">
            {invitation.isSystemInvite ? "הזמנה ליצירת משרד" : "הזמנה להצטרפות למשרד"}
          </CardTitle>
          <CardDescription>
            {invitation.isSystemInvite
              ? "הוזמנת ליצור משרד חדש ב-Legalix"
              : `הוזמנת להצטרף למשרד "${invitation.officeName}"`}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">דוא"ל</span>
              <span className="font-medium">{invitation.email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">תפקיד</span>
              <Badge variant="outline">
                {invitation.role === "office_admin" ? "מנהל משרד" : "משתמש"}
              </Badge>
            </div>
            {invitation.officeName && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">משרד</span>
                <span className="font-medium">{invitation.officeName}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">תוקף</span>
              <span className="font-medium">
                {new Date(invitation.expiresAt).toLocaleDateString("he-IL")}
              </span>
            </div>
          </div>

          {user ? (
            <Button
              className="w-full"
              onClick={handleAccept}
              disabled={accepting}
            >
              {accepting ? (
                <>
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                  מעבד...
                </>
              ) : (
                <>
                  <CheckCircle className="ml-2 h-4 w-4" />
                  קבל הזמנה
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-center text-muted-foreground">
                יש להתחבר כדי לקבל את ההזמנה
              </p>
              <Button className="w-full" onClick={handleLogin}>
                התחבר / הרשם
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitationPage;
